// Pure ECharts option builders for the dashboard chart engine. Each builder
// returns a plain option object (or null for an unsupported type) — no echarts
// import, no DOM, no side effects, and no mutation of the `prepared` inputs (D5).
// Consumed by dashboard-widget.vue echarts branch (see .claude/plans/DASHBOARD_ECHARTS_POC.md).

import { CHART_PALETTE, CHART_TEXT, CHART_STRUCTURE, CHART_HEATMAP_RAMP, stableCategoryColor } from './chartTheme';
import { resolveSchema, resolveDefinition } from '@/features/Dashboard/utils/coloringSchemaEngine';
import {
  RENDER_STATE,
  SCHEMA_KIND,
  APPLIES_TO,
  THEME,
  COLORING_SOURCE,
  SCHEMA_ID,
  getBuiltInSchema,
  convertUnit,
} from '@/features/Dashboard/utils/coloringSchemas';
import { NO_DATA_COLOR } from '@/features/Tags/components/monitoring/cmlColoringConstants';
import { UNSET_LABEL, OTHER_LABEL } from '@/features/Dashboard/service/chart-data.service';
import { DATE_SQL_TYPES, formatDateValue } from '@/features/Dashboard/utils/columnTypes';
import { compactNumber } from '@/features/Dashboard/utils/compactNumber';
import { log as debugLog } from '@/utils/debugLogger';

// New multi-series ("matrix") chart type names. Frozen constants rather than bare
// strings so the registry has one home the wizard, widget, and normalizer all import.
export const STACKED_COLUMN = 'stackedColumn';
// Dual-axis line: category = groupBy[0], one line series PER METRIC, each metric mapped to a
// left or right value axis via axisByMetric.
export const DUAL_AXIS_LINE = 'dualAxisLine';
// Heatmap: x = groupBy[0], y = groupBy[1], cell value = metrics[0]; colored via a continuous
// visualMap ramp (never per-datum itemStyle — the coloring contract).
export const HEATMAP = 'heatmap';
// Mixed comparison: one aligned series per metric over a composite group-by label. One optional
// metric alias is promoted to the dashed reference line; every other metric remains a grouped bar.
export const MIXED_COMPARISON = 'mixedComparison';

// Types that consume a matrix (categories × series) contract instead of the single-series
// prepareChartData arrays. stackedColumn + heatmap pivot the series on groupBy[1]
// (prepareMatrixData); dualAxisLine pivots the series on the metrics (prepareMetricSeriesData) —
// the widget routes to the right normalizer.
export const MATRIX_TYPES = Object.freeze([STACKED_COLUMN, DUAL_AXIS_LINE, HEATMAP, MIXED_COMPARISON]);

// Types that have NO Apex builder by construction (they bypass the backend isChart flatten).
// When the engine falls back to Apex (echartsUnavailable) these MUST render an explicit
// "engine required" state, never Apex's silent blank chrome.
export const ECHARTS_ONLY_TYPES = Object.freeze([STACKED_COLUMN, DUAL_AXIS_LINE, HEATMAP, MIXED_COMPARISON]);

// Chart messageTypes that resolve to the ECharts engine. The engine-resolution
// builder-support check consumes this — an unlisted type falls back to Apex.
export const SUPPORTED_TYPES = Object.freeze(['bar', 'column', 'line', 'area', 'pie', 'donut', STACKED_COLUMN, DUAL_AXIS_LINE, HEATMAP, MIXED_COMPARISON]);

// Orientation tokens threaded to the shared bar/pie color + geometry helpers. Distinct from the
// messageType switch: 'donut' shares a builder with 'pie' and 'area' with 'line', so orientation
// collapses to these three. Frozen so the value has one home instead of bare-literal call sites.
const ORIENTATION = Object.freeze({ BAR: 'bar', COLUMN: 'column', PIE: 'pie' });

// fontSize setting (0|1|2) → px, falling back to a safe medium default for
// undefined / out-of-range values (the widget computed yields undefined there).
function resolveFontSizePx(fontSize) {
  const map = CHART_TEXT.FONT_SIZE_PX;
  return Object.prototype.hasOwnProperty.call(map, fontSize)
    ? map[fontSize]
    : CHART_TEXT.FONT_SIZE_DEFAULT_PX;
}

// Inner-text px for the widget-size tier, keyed off the SAME legacy fontSize (0/1/2, absent = 1)
// the legend scale reads. map[1] holds today's fixed value, so a normal/absent widget stays
// byte-identical; only 0/2 deviate. Distinct from resolveFontSizePx (the FONT_SIZE_PX legend /
// general-text scale); axis labels, series value labels and the donut center ride their own ramps.
function tierInnerPx(map, fontSize) {
  return Object.prototype.hasOwnProperty.call(map, fontSize) ? map[fontSize] : map[1];
}

// Axis tick label / bar-column value label / donut center sub-label / heatmap visualMap px for the
// tier: 13 / 14 / 16 (small / normal / large). Distinct from the pre-tier fixed
// CHART_STRUCTURE.AXIS_LABEL_SIZE_PX (11), which stays the default only when no ctx is threaded.
function axisLabelPx(ctx) {
  return tierInnerPx(CHART_TEXT.INNER_AXIS_LABEL_PX, ctx && ctx.fontSize);
}

// Color for datum i: an explicit prepared color wins, else cycle the categorical
// palette by index. Resolved per-item — never gated on colors[0].
function resolveItemColor(colors, i) {
  const explicit = colors && colors[i];
  return explicit || CHART_PALETTE[i % CHART_PALETTE.length];
}

// ── Legacy severity/RAG palette → built-in schema auto-bind (§4.7) ───────────────
// The showcase used to paint severity/RAG hexes inline (SEVERITY_COLORS / RAG_COLORS). Those hues
// now live ONLY in the built-in `severity` / `rag` schema definitions (coloringSchemas.js) and are
// applied through the SAME engine the explicit "BY SCHEMA" path uses. A chart with no explicit
// schema AUTO-BINDS to a built-in when its labels VALUE-MATCH that built-in's entries — reproducing
// the legacy primary mechanism (label text, never the column name), with the column name kept only
// as the RAG-vs-severity tiebreak below.

// Normalize a label the way the engine's category matcher does (trim + lowercase), so a coverage
// probe here agrees with the engine lookup downstream (compileCategoryLookup keys identically).
function normalizeLabel(label) {
  return String(label == null ? '' : label).trim().toLowerCase();
}

// Entry-value sets of the two built-in category schemas, read ONCE from their definitions (the
// single source of truth — no label list is duplicated here). Severity covers Low / Medium / High /
// High (Repair-Required); RAG covers the bare High / Medium / Low.
function builtInValueSet(id) {
  const schema = getBuiltInSchema(id);
  return new Set(((schema && schema.entries) || []).map((e) => normalizeLabel(e.value)));
}
const SEVERITY_ENTRY_VALUES = builtInValueSet(SCHEMA_ID.SEVERITY);
const RAG_ENTRY_VALUES = builtInValueSet(SCHEMA_ID.RAG);

// Sentinel slice labels the data pipeline injects (a null/blank group → UNSET_LABEL; small slices →
// OTHER_LABEL). Imported from chart-data.service (single source of truth, so a rename can't silently
// break coverage), normalized here for the same-shape comparison. They are NOT real categories, so
// they must not veto a value-match — a Severity donut with one null-severity row must still color its
// real levels rather than dropping the whole chart to the palette.
const SENTINEL_LABELS = new Set([UNSET_LABEL, OTHER_LABEL].map(normalizeLabel));

// A groupBy column whose NAME denotes severity always uses the 4-level severity
// scale (High = pending amber), whatever levels the current data returns. Keying the
// RAG-vs-severity decision on the column (schema) rather than the returned labels
// (data) keeps a severity donut's colors STABLE across filtering — the Anomaly
// Severity donut (column 'Severity') must never flip High to red when its
// Repair-Required rows are absent. The CUI donut (column 'CUI Susceptibility') does
// not match, so it keeps the RAG treatment. Both verified against STARTER_MANIFEST.
const SEVERITY_COLUMN_PATTERN = /severity/i;
function isSeverityColumn(columnName) {
  return SEVERITY_COLUMN_PATTERN.test(String(columnName == null ? '' : columnName));
}

// The built-in schema id to AUTO-BIND for a chart's labels, or null (→ default palette). Mirrors the
// retired resolvePieColor / resolveBarColor precedence: RAG wins ONLY for a pie/donut on a
// non-severity column whose (non-sentinel) labels are a bare High/Medium/Low set (the old `useRag` —
// so a severity donut stays on the 4-level scale under filtering, and bar/column are always
// severity); otherwise severity when those labels are covered. A 4-level set (incl. 'High
// (Repair-Required)') never matches RAG. Value coverage is the trigger, so a High/Medium/Low column
// under ANY name still colors. Sentinel slices (Unset / Other) are dropped before the coverage test
// so they can't veto the match — the engine paints them grey via the unmapped/no-data path, matching
// the legacy per-datum matcher (which colored the real levels and left sentinels on the palette).
function autoBindSchemaId(labels, groupByColumn, orientation) {
  const allNames = (Array.isArray(labels) ? labels : []).map(normalizeLabel).filter(Boolean);
  const names = allNames.filter((n) => !SENTINEL_LABELS.has(n));
  if (names.length !== allNames.length) {
    debugLog('[echartsOptions] auto-bind coverage: excluding sentinel slice(s)', allNames.length - names.length);
  }
  if (names.length === 0) return null;
  const coveredBy = (valueSet) => names.every((n) => valueSet.has(n));
  if (orientation === ORIENTATION.PIE && !isSeverityColumn(groupByColumn) && coveredBy(RAG_ENTRY_VALUES)) {
    return SCHEMA_ID.RAG;
  }
  if (coveredBy(SEVERITY_ENTRY_VALUES)) return SCHEMA_ID.SEVERITY;
  return null;
}

// Non-schema bar/column per-datum fill: explicit prepared color wins (D5); else horizontal bars take
// the single brand fill under showcase; else cycle the categorical palette. Severity/RAG hues are no
// longer painted here — they flow through the schema auto-bind (resolveSchemaColors). With `showcase`
// false this is byte-identical to resolveItemColor — the sole color path for the flag-off builds.
function barFallbackColor(colors, i, showcase, orientation, labels) {
  const explicit = colors && colors[i];
  if (explicit) return explicit;
  if (showcase && orientation === ORIENTATION.BAR) return CHART_STRUCTURE.BAR.SINGLE_COLOR;
  // Value-stable categorical hue (RANK of the value in the full label set) so a category's chart
  // colour is DISTINCT and matches its 3D-isolate colour (both resolve via stableCategoryColor over
  // the same value set). Falls back to the index-based resolver when no labels are available.
  const label = labels && labels[i];
  return label != null ? stableCategoryColor(label, labels) : resolveItemColor(colors, i);
}

// Convert a #rrggbb hex to an rgba() string. echarts (zrender) parses rgba()
// reliably across builds, unlike 8-digit hex — used only for gradient stops.
function hexToRgba(hex, alpha) {
  const h = String(hex).replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ─── Coloring-schema adapter (§4.7 matrix, §4.4 states) ──────────────────────────
// Consumes a widget's schema binding from `ctx` and produces per-datum chart FILLS (or, for
// line/area, `markArea` band backgrounds) through the SAME engine the KPI path uses — no schema
// matching is re-implemented here. Every branch is gated on `ctx.coloringSource === 'schema'`;
// with it absent the functions return null/undefined so every existing color path runs untouched
// and the emitted option is byte-identical to today (proven by a before/after deep-diff harness).
//
// CTX CONTRACT (the consumer wiring seam): `dashboard-widget.vue` must pass, alongside today's
// ctx, `coloringSource` + `schemaSelection` (from the widget's sqlViewConfig, same keys the KPI
// evaluator reads) + `theme` ('light'|'dark' from $vuetify.theme.dark) + optional `bands`/
// `bandsError`/`facility` for band-backed built-ins. That hop is NOT wired yet, so charts render
// schema colors only once it lands (see the Step-5 report). This adapter is the stable surface it
// wires against.

// Semantic tone → chart FILL hex pair. The engine leaves tone-backed matches unresolved
// (resolvedColor null) so each surface renders the tone itself; for chart fills (§4.9 permits raw
// hex as a fill) we resolve to the shipped WCAG pairs — the same values the KPI tone classes use
// (CustomDashboard.vue:2772-2814). Used only as a fallback, so a future engine that populates
// resolvedColor for tones is auto-adopted.
const TONE_FILL = Object.freeze({
  good: { light: '#03624c', dark: '#2cc295' },
  caution: { light: '#a07a00', dark: '#d2a000' },
  critical: { light: '#b71c1c', dark: '#ef5350' },
});

// A schema's form (category vs number) from the selection, so the adapter buckets the right datum
// field (category → group label, number → measure value). Reads only the schema's TYPE, never its
// bands (the mid-edit surface). Resolves builtin | inline | reference through the shared engine
// helper (threading librarySchemas), so a custom (reference) schema reports its real appliesTo
// instead of falling through to null — the root of "schemas only work on KPIs".
function schemaAppliesTo(selection, librarySchemas) {
  const def = resolveDefinition(selection, librarySchemas);
  return def ? def.appliesTo : null;
}

// One resolved outcome → a chart fill hex. Raw-color matches and neutral states already carry a
// resolvedColor (grey for noData/unmapped); tone matches don't, so fall back to the tone pair.
// Exported so the 3D-isolation colour resolver (isolateColor.js) paints elements from the SAME
// schema outcome -> hex mapping the chart fill uses, keeping isolate and fill colours identical.
export function outcomeFill(outcome, theme) {
  if (!outcome) return null;
  if (outcome.resolvedColor) return outcome.resolvedColor;
  if (outcome.tone && TONE_FILL[outcome.tone]) return TONE_FILL[outcome.tone][theme === THEME.DARK ? 'dark' : 'light'];
  return null;
}

function schemaTheme(ctx) {
  return ctx && ctx.theme === THEME.DARK ? THEME.DARK : THEME.LIGHT;
}

// Per-datum schema fills for a grouped/valued series (bar / column / pie / donut), in ORIGINAL
// datum order so they ride the horizontal-bar reversal like prepared.colors. Returns:
//   { colors: string[] } when the schema resolves (matched / noData / unmapped grey per datum),
//   { warn: true }       when the whole schema is unavailable (caller keeps the default palette
//                        and shows a warning, §4.4), or
//   null                 when coloringSource !== 'schema' (every existing path runs untouched).
function schemaSeriesColors(prepared, ctx) {
  if (!ctx || ctx.coloringSource !== COLORING_SOURCE.SCHEMA || !ctx.schemaSelection) return null;
  const theme = schemaTheme(ctx);
  // librarySchemas resolves a `reference` selection to the dashboard's saved schema (folded into the
  // engine's cache key, so no memo collision); ignored for builtin / inline.
  const lookup = resolveSchema(ctx.schemaSelection, {
    theme, bands: ctx.bands, bandsError: ctx.bandsError, facility: ctx.facility,
    librarySchemas: ctx.librarySchemas,
  });
  debugLog('[echartsOptions] schema fill resolve', ctx.schemaSelection.kind, ctx.schemaSelection.schemaId || '(inline)');
  // schemaUnavailable / loadError are CONSTANT lookups (same outcome for every value), so a null
  // probe distinguishes a broken schema from a per-datum noData on a valid one.
  const probe = lookup(null);
  if (probe.state === RENDER_STATE.SCHEMA_UNAVAILABLE || probe.state === RENDER_STATE.LOAD_ERROR) {
    return { warn: true };
  }
  const byCategory = schemaAppliesTo(ctx.schemaSelection, ctx.librarySchemas) === APPLIES_TO.CATEGORY;
  const labels = (prepared && prepared.labels) || [];
  const series = (prepared && prepared.series) || [];
  const n = byCategory ? labels.length : series.length;
  const colors = [];
  for (let i = 0; i < n; i++) {
    const outcome = byCategory
      ? lookup(labels[i])
      : lookup(series[i], { valueUnit: ctx.schemaSelection.valueUnit });
    colors.push(outcomeFill(outcome, theme) || NO_DATA_COLOR);
  }
  return { colors };
}

// Per-datum schema fills for a grouped/valued chart (bar / column / pie / donut), from EITHER source:
//   1. the widget's EXPLICIT "BY SCHEMA" selection (coloringSource === 'schema') — unchanged, and it
//      always wins; or
//   2. under the showcase treatment only, an AUTO-BIND to the built-in severity / RAG schema when the
//      labels value-match it (the legacy severity/RAG coloring, now schema-sourced).
// Returns the schemaSeriesColors result ({ colors } | { warn }) or null (→ the default palette runs,
// byte-identically). `orientation` is 'bar' | 'column' | 'pie' — it scopes the RAG-vs-severity
// tiebreak (RAG is pie/donut-only, mirroring the retired useRag). Explicit prepared per-datum colors
// (D5) still win over an AUTO-BIND fill (they were already overridden by an explicit selection).
function resolveSchemaColors(prepared, ctx, orientation) {
  const explicit = schemaSeriesColors(prepared, ctx);
  if (explicit) return explicit;
  if (!ctx || !ctx.showcase) return null;
  const labels = (prepared && prepared.labels) || [];
  const schemaId = autoBindSchemaId(labels, ctx.groupByColumn, orientation);
  if (!schemaId) return null;
  debugLog('[echartsOptions] severity/RAG auto-bind', schemaId, orientation, ctx.groupByColumn || '');
  const auto = schemaSeriesColors(prepared, {
    ...ctx,
    coloringSource: COLORING_SOURCE.SCHEMA,
    schemaSelection: { kind: SCHEMA_KIND.BUILTIN, schemaId },
  });
  if (!auto || !auto.colors) return auto;
  // D5: an explicit prepared per-datum color still wins over the auto-bound fill (legacy parity).
  const prepColors = (prepared && prepared.colors) || [];
  return { colors: auto.colors.map((c, i) => prepColors[i] || c) };
}

// The number-schema DEFINITION behind a selection — inline embeds it, built-ins are static, and a
// reference resolves from librarySchemas (all via the shared engine helper). null for category
// schemas, an unresolvable selection, or a number schema with no authored ranges.
function numberSchemaDef(selection, librarySchemas) {
  const def = resolveDefinition(selection, librarySchemas);
  if (!def || def.appliesTo !== APPLIES_TO.NUMBER || !Array.isArray(def.ranges) || !def.ranges.length) return null;
  return def;
}

// markArea band backgrounds for a line/area trend (§4.7: the line color is NOT schema-driven;
// horizontal bands are drawn behind it). Works for any number schema (inline or built-in). Range
// boundaries are in the schema's unit; the trend's y-axis is in the measure's unit (valueUnit), so
// boundaries are converted for placement, and each band's fill is resolved through the engine at an
// interior MEASURE value (color resolution stays in the engine). null when the schema is not a
// resolvable number schema (category schema on a trend, reference, or missing → caller warns).
function schemaMarkArea(ctx) {
  if (!ctx || ctx.coloringSource !== COLORING_SOURCE.SCHEMA || !ctx.schemaSelection) return null;
  const sel = ctx.schemaSelection;
  const def = numberSchemaDef(sel, ctx.librarySchemas);
  if (!def) return null;
  const theme = schemaTheme(ctx);
  const lookup = resolveSchema(sel, { theme, librarySchemas: ctx.librarySchemas });
  const valueUnit = sel.valueUnit || def.unit;
  const toMeasure = b => (b == null ? null : convertUnit(b, def.unit, valueUnit));
  const data = def.ranges.map((r) => {
    const from = toMeasure(r.from);
    const to = toMeasure(r.to);
    const interior = from == null
      ? (to == null ? 0 : to - 1)
      : (to == null ? from + 1 : (from + to) / 2);
    const fill = outcomeFill(lookup(interior, { valueUnit }), theme) || NO_DATA_COLOR;
    // Unbounded ends anchor to the axis extremes ('min'/'max'), NOT undefined — a null lower end on
    // a schema with negative values (e.g. the Remaining Life end-of-life band) must reach the axis
    // bottom, not clamp at 0. (Exact placement is a Step-6 live-ECharts verification item.)
    return [
      { yAxis: from == null ? 'min' : from, itemStyle: { color: hexToRgba(fill, 0.12) } },
      { yAxis: to == null ? 'max' : to },
    ];
  });
  return { silent: true, data };
}

// Append a small, unobtrusive warning when a chart's chosen schema can't be resolved (§4.4
// schemaUnavailable/loadError → default palette + visible warning). Appends to option.graphic so
// it never clobbers the donut center total.
function applySchemaWarning(option) {
  const warn = {
    type: 'text',
    right: 8,
    top: 6,
    style: { text: 'Schema colors unavailable', fill: CHART_TEXT.MUTED, fontSize: 11, fontFamily: CHART_TEXT.FONT_FAMILY },
  };
  option.graphic = Array.isArray(option.graphic) ? [...option.graphic, warn] : [warn];
}

// Legend anchor props for a position keyword.
function legendPosition(position) {
  switch (position) {
    case 'top':
      return { top: 0, left: 'center' };
    case 'left':
      return { left: 0, top: 'middle', orient: 'vertical' };
    case 'right':
      return { right: 0, top: 'middle', orient: 'vertical' };
    case 'bottom':
    default:
      return { bottom: 0, left: 'center' };
  }
}

// Legend block. mini mode suppresses it unconditionally; otherwise it honors
// ctx.legend {show, position}. Cartesian builders still force show:false (D12).
function buildLegend(ctx) {
  const legend = ctx && ctx.legend;
  const mini = !!(ctx && ctx.mini);
  const position = (legend && legend.position) || CHART_STRUCTURE.LEGEND.DEFAULT_POSITION;
  return {
    show: !mini && !!(legend && legend.show),
    itemGap: CHART_STRUCTURE.LEGEND.ITEM_GAP,
    icon: CHART_STRUCTURE.LEGEND.ICON,
    textStyle: { color: CHART_TEXT.MUTED },
    ...legendPosition(position),
  };
}

// Category axis from labels. Slices labels into a fresh array so the input is
// never mutated.
function categoryAxis(labels, axisPx = CHART_STRUCTURE.AXIS_LABEL_SIZE_PX) {
  return {
    type: 'category',
    data: Array.isArray(labels) ? labels.slice() : [],
    axisLabel: { color: CHART_TEXT.MUTED, fontSize: axisPx },
    axisLine: { lineStyle: { color: CHART_STRUCTURE.AXIS_LINE_COLOR } },
    axisTick: { show: false },
  };
}

function isDateDataType(dataType) {
  return DATE_SQL_TYPES.includes(String(dataType || '').toLowerCase());
}

function compactDateAxisLabel(value) {
  const formatted = formatDateValue(value);
  return formatted.endsWith(' 00:00') ? formatted.slice(0, -6) : formatted;
}

// Numeric value axis. Showcase compact-formats the tick labels (2.5K form ≥1000);
// with `showcase` falsy the axis is byte-identical to the flag-off build.
function valueAxis(showcase, axisPx = CHART_STRUCTURE.AXIS_LABEL_SIZE_PX) {
  const axis = {
    type: 'value',
    axisLabel: { color: CHART_TEXT.MUTED, fontSize: axisPx },
    splitLine: { lineStyle: { color: CHART_STRUCTURE.GRID_LINE_COLOR } },
    axisLine: { show: false },
    axisTick: { show: false },
  };
  if (showcase) axis.axisLabel.formatter = compactNumber;
  return axis;
}

// Bar/column data points: per-item colored, with border-radius applied to the
// value end (orientation-dependent, ECharts corner order [TL, TR, BR, BL]). Color
// resolves via barFallbackColor, which is byte-identical to resolveItemColor when
// showcase is falsy.
function barSeriesData(prepared, borderRadius, showcase, orientation, schemaColors) {
  const series = (prepared && prepared.series) || [];
  const colors = (prepared && prepared.colors) || [];
  return series.map((value, i) => ({
    value,
    itemStyle: {
      // Schema fill (explicit BY SCHEMA or a showcase auto-bind) wins per §4.7 precedence; null
      // schemaColors (every non-schema, non-auto-bind path) falls through to the existing resolver,
      // byte-identically.
      color: (schemaColors && schemaColors[i]) || barFallbackColor(colors, i, showcase, orientation, prepared && prepared.labels),
      borderRadius,
    },
  }));
}

// Series-level `select` styling (applied to a selected datum). A contrasting
// rich-black border + soft shadow rather than a fill color, so the selection
// reads on every palette hue. Each series also sets `selectedMode: 'single'`
// (W3) so this — and EChartsWidget's dispatched select/unselect — take effect.
function selectScaffold() {
  return {
    itemStyle: {
      borderColor: CHART_STRUCTURE.SELECT_ACCENT,
      borderWidth: 2,
      shadowBlur: CHART_STRUCTURE.SELECT_SHADOW_BLUR,
      shadowColor: CHART_STRUCTURE.SELECT_SHADOW_COLOR,
    },
  };
}

// Series-level `emphasis` styling — applies on hover AND on the programmatic
// highlight() dispatch (same channel). Moderate shadow so a highlight reads
// without hover feeling heavier than Apex.
function emphasisScaffold() {
  return {
    itemStyle: {
      shadowBlur: CHART_STRUCTURE.EMPHASIS_SHADOW_BLUR,
      shadowColor: CHART_STRUCTURE.EMPHASIS_SHADOW_COLOR,
    },
  };
}

// Resolve the color for each datum into a CONCRETE array in ORIGINAL data order,
// so the first data item keeps CHART_PALETTE[0] regardless of later render order.
// The horizontal bar builder materializes BEFORE reversing (W5.1b) — otherwise the
// per-index fallback would map the palette to the reversed positions. Under showcase
// this bakes the castleton single fill (index-independent, so reversal keeps it paired);
// severity/RAG fills ride the separate schemaColors array. Fresh array; byte-identical to
// the old resolveItemColor path when showcase falsy.
function materializeColors(prepared, showcase, orientation) {
  const series = (prepared && prepared.series) || [];
  const colors = prepared && prepared.colors;
  return series.map((_, i) => barFallbackColor(colors, i, showcase, orientation, prepared && prepared.labels));
}

// Reverse the co-indexed prepared arrays into a FRESH object (never mutates the
// input, D5). Used by the horizontal bar builder so the highest value reads at
// the top (ECharts places yAxis category index 0 at the bottom).
function reversePrepared(prepared) {
  if (!prepared) return prepared;
  const rev = a => (Array.isArray(a) ? a.slice().reverse() : a);
  return {
    labels: rev(prepared.labels),
    series: rev(prepared.series),
    colors: rev(prepared.colors),
  };
}

// Shared cartesian base: text / tooltip / grid / legend defaults. Callers add
// axes + series and may force legend.show off.
function buildBase(ctx) {
  const fontPx = resolveFontSizePx(ctx && ctx.fontSize);
  return {
    textStyle: {
      fontFamily: CHART_TEXT.FONT_FAMILY,
      color: CHART_TEXT.MUTED,
      fontSize: fontPx,
    },
    grid: {
      top: CHART_STRUCTURE.GRID.top,
      right: CHART_STRUCTURE.GRID.right,
      bottom: CHART_STRUCTURE.GRID.bottom,
      left: CHART_STRUCTURE.GRID.left,
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
    },
    legend: buildLegend(ctx),
  };
}

// One bar/column series object (both use ECharts series type 'bar'; orientation
// is expressed by which axis is the category axis, set by the caller). Showcase adds
// a compact-formatted value label and a per-orientation width cap; the flag-off path
// is the untouched original series object.
function barSeries(prepared, borderRadius, showcase, orientation, schemaColors, labelPx = CHART_STRUCTURE.AXIS_LABEL_SIZE_PX) {
  const series = {
    type: 'bar',
    barMaxWidth: CHART_STRUCTURE.BAR.MAX_WIDTH,
    data: barSeriesData(prepared, borderRadius, showcase, orientation, schemaColors),
    selectedMode: 'single',
    select: selectScaffold(),
    emphasis: emphasisScaffold(),
  };
  if (showcase) {
    series.barMaxWidth = orientation === ORIENTATION.BAR
      ? CHART_STRUCTURE.BAR.SHOWCASE_MAX_WIDTH_BAR
      : CHART_STRUCTURE.BAR.SHOWCASE_MAX_WIDTH_COLUMN;
    series.label = {
      show: true,
      position: orientation === ORIENTATION.BAR ? 'right' : 'top',
      color: CHART_TEXT.MUTED,
      fontSize: labelPx,
      formatter: p => compactNumber(p.value),
    };
  }
  return series;
}

// Horizontal bars: category on the y-axis, value on the x-axis.
function buildBar(prepared, ctx) {
  const base = buildBase(ctx);
  base.legend.show = false; // D12: single-series cartesian legend is non-filtering
  const r = CHART_STRUCTURE.BAR.BORDER_RADIUS;
  const showcase = !!(ctx && ctx.showcase);
  const axisPx = axisLabelPx(ctx);
  // Showcase widens grid.right so right-positioned value labels don't clip.
  if (showcase) Object.assign(base.grid, CHART_STRUCTURE.GRID_SHOWCASE_BAR);
  // ECharts places yAxis category index 0 at the BOTTOM; reverse so the highest
  // value reads at the TOP, matching Apex's top-down horizontal bars. Purely
  // presentational — labels/values/colors stay index-paired after the reversal, so
  // tooltips, chart-select labels, and highlight lookups are unaffected. Reverses
  // copies, never the input (D5). Column is unchanged (vertical order is correct).
  // Materialize the palette fallback in ORIGINAL order FIRST (W5.1b) so the first
  // data item keeps CHART_PALETTE[0] (castleton) at the top, not the reversed hue.
  const materialized = { ...prepared, colors: materializeColors(prepared, showcase, ORIENTATION.BAR) };
  const ordered = reversePrepared(materialized);
  // Schema fills (explicit BY SCHEMA or a showcase severity/RAG auto-bind) are computed in ORIGINAL
  // order, then reversed to stay index-paired with the reversed bar data (like the materialized
  // palette above). Null when neither applies.
  const schema = resolveSchemaColors(prepared, ctx, ORIENTATION.BAR);
  const revSchemaColors = schema && schema.colors ? schema.colors.slice().reverse() : null;
  const option = {
    ...base,
    xAxis: valueAxis(showcase, axisPx),
    yAxis: categoryAxis(ordered && ordered.labels, axisPx),
    series: [barSeries(ordered, [0, r, r, 0], showcase, ORIENTATION.BAR, revSchemaColors, axisPx)],
  };
  if (schema && schema.warn) applySchemaWarning(option);
  return option;
}

// Vertical columns: category on the x-axis, value on the y-axis.
function buildColumn(prepared, ctx) {
  const base = buildBase(ctx);
  base.legend.show = false; // D12: single-series cartesian legend is non-filtering
  const r = CHART_STRUCTURE.BAR.BORDER_RADIUS;
  const showcase = !!(ctx && ctx.showcase);
  const axisPx = axisLabelPx(ctx);
  // Showcase adopts the canonical column bottom/left insets (c-equip, :702).
  if (showcase) Object.assign(base.grid, CHART_STRUCTURE.GRID_SHOWCASE_COLUMN);
  const schema = resolveSchemaColors(prepared, ctx, ORIENTATION.COLUMN);
  const schemaColors = schema && schema.colors;
  const option = {
    ...base,
    xAxis: categoryAxis(prepared && prepared.labels, axisPx),
    yAxis: valueAxis(showcase, axisPx),
    series: [barSeries(prepared, [r, r, 0, 0], showcase, ORIENTATION.COLUMN, schemaColors, axisPx)],
  };
  if (schema && schema.warn) applySchemaWarning(option);
  return option;
}

// Single-series line/area. Straight segments, no markers, brand-primary color.
// `area` adds a top-to-transparent vertical gradient expressed as the plain-object
// linear-gradient form (no echarts.graphic import — the builders stay pure).
function lineSeries(prepared, area) {
  const series = (prepared && prepared.series) || [];
  const primary = CHART_PALETTE[0];
  // A polyline needs ≥2 points; with fewer, symbol:'none' renders nothing at
  // all — show the point marker for tiny series so a one-datum widget isn't blank.
  const showPointMarkers = series.filter(v => v != null).length < 2;
  const s = {
    type: 'line',
    symbol: showPointMarkers ? 'circle' : 'none',
    symbolSize: 8,
    lineStyle: { width: 2, color: primary },
    itemStyle: { color: primary },
    data: series.slice(),
    selectedMode: 'single',
    select: selectScaffold(),
    emphasis: emphasisScaffold(),
  };
  if (area) {
    s.areaStyle = {
      color: {
        type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
        colorStops: [
          { offset: 0, color: hexToRgba(primary, 0.35) },
          { offset: 1, color: hexToRgba(primary, 0.02) },
        ],
      },
    };
  }
  return s;
}

// Line and area share a builder (area toggles the gradient fill). Category xAxis
// hugs the edges (boundaryGap off); legend hidden like the other cartesian types.
function buildLineLike(prepared, ctx, area) {
  const base = buildBase(ctx);
  base.legend.show = false; // D12: single-series cartesian legend is non-filtering
  base.tooltip.axisPointer = { type: 'line' }; // a vertical line suits line/area
  const axisPx = axisLabelPx(ctx);
  const xAxis = categoryAxis(prepared && prepared.labels, axisPx);
  xAxis.boundaryGap = false;
  if (isDateDataType(ctx && ctx.groupByDataType)) {
    xAxis.axisLabel.formatter = compactDateAxisLabel;
    xAxis.axisLabel.alignMinLabel = 'left';
    xAxis.axisLabel.alignMaxLabel = 'right';
  }
  const s = lineSeries(prepared, area);
  // §4.7: the line color is NOT schema-driven; a number schema paints horizontal band backgrounds.
  const bands = schemaMarkArea(ctx);
  if (bands) s.markArea = bands;
  const option = {
    ...base,
    xAxis,
    yAxis: valueAxis(undefined, axisPx),
    series: [s],
  };
  // Schema chosen but no bands could be drawn (a built-in number schema — the engine exposes no
  // band accessor — or a non-number schema on a trend): keep the default line + a warning (§4.4).
  if (ctx && ctx.coloringSource === COLORING_SOURCE.SCHEMA && !bands) applySchemaWarning(option);
  return option;
}

// Pie/donut series. Per-item colored slices with white separators; labels off
// (widgets carry their own titles). select scaffolding pops the slice out
// (selectedOffset) and rings it with the accent — no selectedMode key (W3).
function pieSeries(prepared, radius, showcase, schemaColors) {
  const labels = (prepared && prepared.labels) || [];
  const series = (prepared && prepared.series) || [];
  const colors = (prepared && prepared.colors) || [];
  const s = {
    type: 'pie',
    radius,
    avoidLabelOverlap: true,
    label: { show: false },
    labelLine: { show: false },
    itemStyle: { borderColor: '#fff', borderWidth: 2 },
    selectedMode: 'single',
    selectedOffset: 8,
    select: selectScaffold(),
    emphasis: emphasisScaffold(),
    data: labels.map((name, i) => ({
      name,
      value: series[i],
      // Schema fill (explicit BY SCHEMA or a showcase severity/RAG auto-bind) wins per §4.7; null
      // schemaColors (every non-schema path) falls through to an explicit prepared color or the
      // categorical palette, byte-identically.
      itemStyle: { color: (schemaColors && schemaColors[i]) || resolveItemColor(colors, i) },
    })),
  };
  // Showcase center is set by buildPieLike (showcasePieCenter): the ring moves with the
  // legend's reserved lane instead of assuming the bottom strip.
  return s;
}

// Showcase ring center per legend placement. The legend RESERVES a lane (bottom strip,
// side band, top strip) and the ring shifts away from it so neither overlaps the other;
// a hidden legend frees every lane and the ring takes the true center.
function showcasePieCenter(position) {
  switch (position) {
    case 'top':
      return ['50%', '57%'];
    case 'left':
      return ['60%', '50%'];
    case 'right':
      return ['40%', '50%'];
    case 'bottom':
      return ['50%', '43%'];
    default:
      return ['50%', '50%'];
  }
}

// Donut center total: integers as-is, else rounded to 2 decimals (mirrors gaugeOption.js
// formatValue), so a SUM over decimal readings renders 0.3, not float noise like 0.30000000000000004.
// `total` is always finite here; the guard keeps parity with formatValue.
function formatDonutTotal(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

// Showcase pie/donut legend: the showcase visual system (single-row SCROLL pager so long
// category lists never wrap over the chart, roundRect swatches, labels truncated at a fixed
// width with a hover tooltip) anchored per the widget's persisted legend {show, position} —
// the wizard's Show legend / Legend position controls own both. An unknown or absent
// position falls back to the bottom-center default via legendPosition(), so a legacy junk
// value can't produce a broken anchor; an absent legend object stays visible (legacy
// showcase widgets predate the toggle).
function buildShowcasePieLegend(ctx) {
  const legend = ctx && ctx.legend;
  const position = (legend && legend.position) || CHART_STRUCTURE.LEGEND.DEFAULT_POSITION;
  const anchors = legendPosition(position);
  // Side legends get a small inset (flush-to-edge reads as misaligned next to the card's
  // padded content) and a tighter truncation width so the label column stays a narrow band.
  if (position === 'left') anchors.left = 8;
  if (position === 'right') anchors.right = 8;
  const vertical = anchors.orient === 'vertical';
  return {
    show: legend ? !!legend.show : true,
    type: 'scroll',
    orient: 'horizontal',
    icon: 'roundRect',
    itemWidth: 11,
    itemHeight: 11,
    itemGap: vertical ? 8 : 10,
    textStyle: { color: CHART_TEXT.MUTED, fontSize: axisLabelPx(ctx), overflow: 'truncate', width: vertical ? 132 : 160 },
    tooltip: { show: true },
    ...anchors,
  };
}

// Attach the showcase donut center total + unit label to the pie series. Height-independent: the
// number+label ride the series label at position:'center', which ECharts anchors on the ring's OWN
// center ['50%','43%'] at render time, so the block's midpoint locks to the hole at ANY container
// height (no px-to-percent math, the drift the old graphic-offset had). The formatter returns the
// two-line rich block for the first datum only and '' for the rest, so it renders ONCE, centered,
// always visible. Both lines take the darkest chart ink; tight 3px gap via the label's top padding.
// Caller gates on showcase + isDonut + !mini + additiveTotal (the total is meaningful only for
// additive COUNT/SUM aggregations).
function applyDonutCenterLabel(option, prepared, ctx) {
  const values = (prepared && prepared.series) || [];
  const total = values.reduce((sum, v) => sum + (Number(v) || 0), 0);
  // Generic, honest unit: the builder can't know the domain noun per widget, so a caller-supplied
  // ctx.centerLabel wins, else a neutral 'total'.
  const centerLabel = (ctx && ctx.centerLabel) || 'total';
  // Capitalize the label's first letter generically (canvas text; e.g. 'total' -> 'Total').
  const centerLabelCap = centerLabel ? centerLabel.charAt(0).toUpperCase() + centerLabel.slice(1) : centerLabel;
  const totalPx = tierInnerPx(CHART_TEXT.INNER_DONUT_TOTAL_PX, ctx && ctx.fontSize);
  const labelPx = tierInnerPx(CHART_TEXT.INNER_DONUT_LABEL_PX, ctx && ctx.fontSize);
  option.series[0].label = {
    show: true,
    position: 'center',
    formatter: (p) => (p && p.dataIndex === 0) ? `{num|${formatDonutTotal(total)}}\n{lbl|${centerLabelCap}}` : '',
    rich: {
      num: { fontSize: totalPx, fontWeight: 600, color: CHART_TEXT.STRONG, fontFamily: CHART_TEXT.FONT_FAMILY, lineHeight: totalPx },
      lbl: { fontSize: labelPx, color: CHART_TEXT.STRONG, fontFamily: CHART_TEXT.FONT_FAMILY, lineHeight: labelPx, padding: [3, 0, 0, 0] },
    },
  };
  option.series[0].labelLine = { show: false };
}

// Pie ('68%') and donut (['46%','68%']) — non-cartesian, so no grid/axes. Legend
// honors ctx.legend (mini still wins → suppressed); item tooltip shows percent.
// Showcase treatment (flag-on, delta 1-4): severity colormap, centered ring, a
// bottom square-marker legend, and — for donut — a center total + unit label.
function buildPieLike(prepared, ctx, radius) {
  const fontPx = resolveFontSizePx(ctx && ctx.fontSize);
  const showcase = !!(ctx && ctx.showcase);
  const mini = !!(ctx && ctx.mini);
  const isDonut = Array.isArray(radius);
  // Showcase RESERVES the legend's space instead of overlaying it: the ring lifts and
  // shrinks (center/radius here + in pieSeries) so the plot area ends above the bottom
  // legend strip. Solid pie shrinks to 62% to leave the same bottom room a donut gets.
  // Match the donut's OUTER radius + center to the (working) pie so their legend layout is
  // identical. The donut's larger 70% ring was extending into the reserved bottom lane,
  // leaving the scroll legend no room to lay out — so it silently rendered nothing while
  // the pie (62% ring) had room. Inner hole 40% keeps space for the center total.
  const seriesRadius = showcase
    ? (isDonut ? ['40%', '62%'] : '62%')
    : radius;

  const legend = (showcase && !mini) ? buildShowcasePieLegend(ctx) : buildLegend(ctx);

  const schema = resolveSchemaColors(prepared, ctx, ORIENTATION.PIE);
  const schemaColors = schema && schema.colors;
  const option = {
    textStyle: {
      fontFamily: CHART_TEXT.FONT_FAMILY,
      color: CHART_TEXT.MUTED,
      fontSize: fontPx,
    },
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend,
    series: [pieSeries(prepared, seriesRadius, showcase, schemaColors)],
  };
  if (showcase) {
    const legendConf = ctx && ctx.legend;
    const legendShown = legend && legend.show !== false;
    const position = legendShown
      ? ((legendConf && legendConf.position) || CHART_STRUCTURE.LEGEND.DEFAULT_POSITION)
      : null;
    option.series[0].center = showcasePieCenter(position);
  }

  // The center total is only drawn for additive aggregations (COUNT/SUM). For AVG/MIN/MAX the caller
  // sets ctx.additiveTotal false, so the ring keeps a clean empty hole rather than showing the
  // meaningless sum of per-group results.
  if (showcase && isDonut && !mini && ctx && ctx.additiveTotal) {
    applyDonutCenterLabel(option, prepared, ctx);
  }
  if (schema && schema.warn) applySchemaWarning(option);
  return option;
}

// Shared unpacking preamble for the three matrix builders: the cartesian base (callers may further
// mutate its tooltip/legend), the tier axis px, and the matrix's categories + series arrays.
function matrixBase(ctx, matrix) {
  return {
    base: buildBase(ctx),
    axisPx: axisLabelPx(ctx),
    categories: (matrix && matrix.categories) || [],
    matrixSeries: (matrix && matrix.series) || [],
  };
}

// Stacked / grouped column: one 'bar' series per groupBy[1] value across the groupBy[0]
// categories. Consumes the prepareMatrixData shape ({ categories, series:[{ name, data }] })
// rather than the single-series prepared arrays. Unlike the single-series cartesian builders
// it does NOT force the legend off (D12): a multi-series chart's legend names the stacked
// series and toggling them is meaningful. stackMode 'group' renders side-by-side columns;
// anything else (default) stacks them (stack:'total'). Series colors cycle the categorical
// palette — the schema-coloring adapter is intentionally not wired here (matrix schema
// coloring is out of this wave's scope). null/absent data points render as gaps (no segment),
// distinct from a measured 0.
function buildStackedColumn(matrix, ctx) {
  const { base, axisPx, categories, matrixSeries } = matrixBase(ctx, matrix);
  const showcase = !!(ctx && ctx.showcase);
  const grouped = !!(ctx && ctx.stackMode === 'group');
  const series = matrixSeries.map((s, i) => {
    const out = {
      type: 'bar',
      name: s && s.name,
      barMaxWidth: CHART_STRUCTURE.BAR.MAX_WIDTH,
      data: Array.isArray(s && s.data) ? s.data.slice() : [],
      itemStyle: { color: CHART_PALETTE[i % CHART_PALETTE.length] },
      // Matrix types are excluded from cross-filter selection, so the native select highlight
      // is disabled (clicking a segment otherwise shows a stray highlight with no filter behind it).
      selectedMode: false,
      emphasis: emphasisScaffold(),
    };
    if (!grouped) out.stack = 'total';
    return out;
  });
  return {
    ...base,
    xAxis: categoryAxis(categories, axisPx),
    yAxis: valueAxis(showcase, axisPx),
    series,
  };
}

// Left + right value axes for a dual-axis chart. A WRAPPER around valueAxis() (its single-axis
// signature is untouched) — calls it twice for two fresh axis objects, then flips the second to
// the right side with its split lines hidden so the two grids don't overprint.
function dualValueAxes(showcase, axisPx = CHART_STRUCTURE.AXIS_LABEL_SIZE_PX) {
  const left = valueAxis(showcase, axisPx);
  const right = valueAxis(showcase, axisPx);
  right.position = 'right';
  right.splitLine = { show: false };
  return [left, right];
}

// Line-type tokens a metric style may carry → ECharts series-line.lineStyle.type. Anything else
// (absent / unknown) falls back to 'solid' — ECharts' own default, so an unstyled metric is
// byte-identical to the pre-styling render.
const DUAL_AXIS_LINE_TYPES = Object.freeze(['solid', 'dashed', 'dotted']);
function seriesLineType(style) {
  return style && DUAL_AXIS_LINE_TYPES.includes(style.lineType) ? style.lineType : 'solid';
}

// Dual-axis line: one line series per metric across the groupBy[0] categories, each metric mapped
// to the left (yAxisIndex 0) or right (yAxisIndex 1) value axis. Consumes the prepareMetricSeriesData
// shape ({ categories, series:[{ name, data }] }) where each series name is the metric ALIAS. The
// alias stays the internal key for the axis lookup (ctx.axisByMetric[alias]) AND the per-metric style
// lookup (ctx.seriesStyles[alias]); the DISPLAY name (legend + tooltip) is the friendly
// ctx.seriesLabels[alias] ("Average of Corrosion Rate") when supplied, else the alias. Legend is left
// ON (multi-series) and follows the resolved series color. Each series takes its color + line type
// from ctx.seriesStyles[alias] when set, else cycles the categorical palette with a solid line (the
// unchanged default). null data points render as gaps (a polyline with <2 real points shows markers
// so a sparse series isn't invisible — mirrors lineSeries).
function buildDualAxisLine(matrix, ctx) {
  const { base, axisPx, categories, matrixSeries } = matrixBase(ctx, matrix);
  base.tooltip.axisPointer = { type: 'line' };
  const showcase = !!(ctx && ctx.showcase);
  const axisByMetric = (ctx && ctx.axisByMetric) || {};
  const seriesLabels = (ctx && ctx.seriesLabels) || {};
  const seriesStyles = (ctx && ctx.seriesStyles) || {};
  const xAxis = categoryAxis(categories, axisPx);
  xAxis.boundaryGap = false;
  const series = matrixSeries.map((s, i) => {
    const alias = s && s.name;
    const style = seriesStyles[alias];
    // A configured hex overrides the palette; an absent/blank color keeps the cycling default.
    const color = (style && typeof style.color === 'string' && style.color) || CHART_PALETTE[i % CHART_PALETTE.length];
    const lineType = seriesLineType(style);
    const data = Array.isArray(s && s.data) ? s.data.slice() : [];
    const showPointMarkers = data.filter(v => v != null).length < 2;
    return {
      type: 'line',
      name: seriesLabels[alias] || alias,
      yAxisIndex: axisByMetric[alias] === 'right' ? 1 : 0,
      symbol: showPointMarkers ? 'circle' : 'none',
      symbolSize: 8,
      lineStyle: { width: 2, color, type: lineType },
      itemStyle: { color },
      data,
      selectedMode: false,
      emphasis: emphasisScaffold(),
    };
  });
  return {
    ...base,
    xAxis,
    yAxis: dualValueAxes(showcase, axisPx),
    series,
  };
}

// Mixed comparison: grouped vertical columns for the measured metrics plus one optional red dashed
// reference line. The first two bar identities use the existing muted-info blue and emerald tokens
// (Design / Measured in the approved demo); further bars continue through the shared palette.
function buildMixedComparison(matrix, ctx) {
  const { base, axisPx, categories, matrixSeries } = matrixBase(ctx, matrix);
  const referenceMetricAlias = (ctx && ctx.referenceMetricAlias)
    || (matrix && matrix.referenceMetricAlias)
    || null;
  const seriesLabels = (ctx && ctx.seriesLabels) || {};
  const barColors = [CHART_PALETTE[2], CHART_PALETTE[1]];
  let barIndex = 0;
  const series = matrixSeries.map((s) => {
    const alias = s && s.name;
    const data = Array.isArray(s && s.data) ? s.data.slice() : [];
    if (alias === referenceMetricAlias) {
      const color = CHART_PALETTE[7];
      return {
        type: 'line',
        name: seriesLabels[alias] || alias,
        symbol: 'none',
        lineStyle: { width: 2, color, type: 'dashed' },
        itemStyle: { color },
        data,
        selectedMode: false,
        emphasis: emphasisScaffold(),
        z: 3,
      };
    }
    const color = barColors[barIndex] || CHART_PALETTE[(barIndex + 2) % CHART_PALETTE.length];
    barIndex += 1;
    return {
      type: 'bar',
      name: seriesLabels[alias] || alias,
      barMaxWidth: CHART_STRUCTURE.BAR.MAX_WIDTH,
      itemStyle: { color },
      data,
      selectedMode: false,
      emphasis: emphasisScaffold(),
    };
  });
  return {
    ...base,
    xAxis: categoryAxis(categories, axisPx),
    yAxis: valueAxis(!!(ctx && ctx.showcase), axisPx),
    series,
  };
}

// Escape HTML metacharacters for the heatmap tooltip, whose formatter returns raw HTML that ECharts
// inserts via innerHTML. Category / series labels are user/customer-controlled register data, so they
// must be escaped to prevent script injection. This is the only HTML-mode formatter in the file.
function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Heatmap: x = groupBy[0] (categories), y = groupBy[1] (series names), cell value = metrics[0].
// Consumes the prepareMatrixData shape. Emits [xIdx, yIdx, value] triples; an ABSENT combination is
// OMITTED entirely (a true no-data cell — never emitted as 0, so no-data reads distinctly from a
// measured 0). COLORING IS visualMap-ONLY: a continuous neutral→severity ramp scaled to the data
// min/max. buildHeatmap never sets per-datum itemStyle and never consumes ctx.schemaColors (the
// coloring contract) — the coloring fold is opt-in inside the legacy builders only. No series legend
// (the visualMap is the scale); tooltip shows the y / x labels + the metric value.
function buildHeatmap(matrix, ctx) {
  // categories = x axis, matrixSeries = y axis (each series is a heatmap row).
  const { base, axisPx, categories, matrixSeries } = matrixBase(ctx, matrix);
  base.tooltip.trigger = 'item';
  base.tooltip.axisPointer = { type: 'none' };
  base.legend.show = false;
  const theme = ctx && ctx.theme === THEME.DARK ? 'dark' : 'light';
  const ramp = CHART_HEATMAP_RAMP[theme] || CHART_HEATMAP_RAMP.light;
  const yNames = matrixSeries.map(s => (s && s.name));
  const valueLabel = (ctx && ctx.valueLabel) || 'value';

  // [xIdx, yIdx, value] triples; absent (null) combinations are omitted → no-data cells.
  const data = [];
  let min = null;
  let max = null;
  for (let y = 0; y < matrixSeries.length; y++) {
    const row = (matrixSeries[y] && matrixSeries[y].data) || [];
    for (let x = 0; x < categories.length; x++) {
      const v = row[x];
      if (v == null) continue;
      data.push([x, y, v]);
      if (min === null || v < min) min = v;
      if (max === null || v > max) max = v;
    }
  }
  // Guard a degenerate visualMap span (echarts divides the ramp by it).
  if (min === null) { min = 0; max = 1; } else if (min === max) { max = min + 1; }

  const xAxis = categoryAxis(categories, axisPx);
  xAxis.splitArea = { show: true };
  const yAxis = categoryAxis(yNames, axisPx);
  yAxis.splitArea = { show: true };

  // "<y> / <x>" then the metric value; d = [xIdx, yIdx, value]. Labels are escaped (user/customer
  // register data rendered by ECharts via innerHTML); d[2] is numeric so it needs no escaping.
  base.tooltip.formatter = (p) => {
    const d = (p && p.data) || [];
    return `${escapeHtml(yNames[d[1]])} / ${escapeHtml(categories[d[0]])}<br/>${escapeHtml(valueLabel)}: ${d[2]}`;
  };

  return {
    ...base,
    // Extra bottom inset so the horizontal visualMap scale clears the x-axis labels.
    grid: { ...base.grid, bottom: 44 },
    xAxis,
    yAxis,
    visualMap: {
      type: 'continuous',
      min,
      max,
      calculable: false,
      orient: 'horizontal',
      left: 'center',
      bottom: 4,
      itemWidth: 12,
      itemHeight: 120,
      textStyle: { color: CHART_TEXT.MUTED, fontSize: axisPx },
      inRange: { color: ramp.slice() },
    },
    series: [{
      type: 'heatmap',
      data,
      selectedMode: false,
    }],
  };
}

/**
 * Build a complete ECharts option object for a chart widget. Pure: reads
 * `prepared` and `ctx`, mutates neither, and returns a fresh option object.
 *
 * @param {string} messageType - widget chart type in SUPPORTED_TYPES ('bar'|'column'|'line'|'area'|'pie'|'donut')
 * @param {{ labels: string[], series: (number|null)[], colors: (string|null)[] }} prepared
 *        co-indexed arrays from prepareChartData() (chart-data.service.js)
 * @param {{ fontSize: (0|1|2|undefined), legend: ({ show: boolean, position: string }|null), mini: boolean, multiSelect?: boolean, showcase?: boolean, groupByColumn?: string, centerLabel?: string, additiveTotal?: boolean }} ctx
 *        fontSize = widget size setting; legend = show/position or null; mini = suppress legend + titles;
 *        multiSelect = dev capability demo (D8) → selectedMode 'multiple' (default single);
 *        showcase = custom-dashboard visual treatment (flag-on) → severity/RAG colors, value labels, showcase grid;
 *        groupByColumn = widget's groupBy column name, keys the stable pie RAG-vs-severity decision (schema, not data);
 *        centerLabel = donut center unit noun (defaults to 'total');
 *        additiveTotal = draw the donut center total only for additive (COUNT/SUM) widgets
 * @returns {Object|null} ECharts option object, or null for an unsupported messageType
 */
export function buildChartOption(messageType, prepared, ctx) {
  let option;
  switch (messageType) {
    case 'bar':
      option = buildBar(prepared, ctx);
      break;
    case 'column':
      option = buildColumn(prepared, ctx);
      break;
    case 'line':
      option = buildLineLike(prepared, ctx, false);
      break;
    case 'area':
      option = buildLineLike(prepared, ctx, true);
      break;
    case 'pie':
      option = buildPieLike(prepared, ctx, '68%');
      break;
    case 'donut':
      option = buildPieLike(prepared, ctx, ['46%', '68%']);
      break;
    case STACKED_COLUMN:
      // `prepared` here is the prepareMatrixData shape ({ categories, series:[{ name, data }] }),
      // not the single-series prepared arrays — routed by the caller (dashboard-widget) which
      // runs prepareMatrixData for MATRIX_TYPES.
      option = buildStackedColumn(prepared, ctx);
      break;
    case DUAL_AXIS_LINE:
      // `prepared` is the prepareMetricSeriesData shape (one series per metric); the caller routes
      // matrix types to the matching normalizer.
      option = buildDualAxisLine(prepared, ctx);
      break;
    case HEATMAP:
      // `prepared` is the prepareMatrixData shape (categories × series); coloring is visualMap-only.
      option = buildHeatmap(prepared, ctx);
      break;
    case MIXED_COMPARISON:
      option = buildMixedComparison(prepared, ctx);
      break;
    default:
      return null;
  }
  // Dev multi-select capability demo (D8) — the ONLY divergence point. Off by
  // default → the builders' 'single' stays untouched, so the option is
  // byte-identical to W3. Never wired to filtering (that would show N selections
  // while the server filters by 1).
  if (ctx && ctx.multiSelect && option && Array.isArray(option.series)) {
    option.series.forEach(s => {
      if (s.selectedMode === false) return; // explicit exclusions (matrix) win over the blanket flip
      s.selectedMode = 'multiple';
    });
  }
  return option;
}
